import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService, type User } from '../services/authService'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  updateUser: (user: Partial<User>) => void
  clearError: () => void
  initializeAuth: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true, // Start with loading true to check auth state
      error: null,
      
      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authService.login({ username, password })
          const { user, access, refresh } = response
          
          // Store tokens in localStorage
          localStorage.setItem('auth-token', access)
          localStorage.setItem('auth-refresh-token', refresh)
          
          set({ 
            user, 
            token: access, 
            isAuthenticated: true, 
            isLoading: false 
          })
        } catch (error: any) {
          set({ 
            error: error.response?.data?.detail || 'Login failed', 
            isLoading: false 
          })
          throw error
        }
      },
      
      logout: async () => {
        try {
          await authService.logout()
        } catch (error) {
          // Logout error - silently fail
        } finally {
          // Clear tokens from localStorage
          localStorage.removeItem('auth-token')
          localStorage.removeItem('auth-refresh-token')
          
          set({ 
            user: null, 
            token: null, 
            isAuthenticated: false,
            error: null
          })
        }
      },
      
      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user
        if (currentUser) {
          set({ user: { ...currentUser, ...userData } })
        }
      },
      
      clearError: () => {
        set({ error: null })
      },
      
      initializeAuth: async () => {
        const token = localStorage.getItem('auth-token')
        if (token) {
          try {
            // Verify token with backend
            const response = await authService.getCurrentUser()
            set({ 
              user: response, 
              token, 
              isAuthenticated: true, 
              isLoading: false 
            })
          } catch (error) {
            // Token is invalid, clear it
            localStorage.removeItem('auth-token')
            localStorage.removeItem('auth-refresh-token')
            set({ 
              user: null, 
              token: null, 
              isAuthenticated: false, 
              isLoading: false 
            })
          }
        } else {
          set({ isLoading: false })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
